import { createContext, useContext } from 'react';
import { isPrerenderPath } from './prerender-paths';

// ─── Language State Management (no translation data) ──────────────

export type Lang = 'en' | 'zh';

/**
 * Detect language. Priority: URL param > localStorage > navigator.
 * URL param is a temporary override — it does NOT persist to localStorage.
 * Only explicit user action (clicking language toggle) writes localStorage.
 */
export function detectLang(): Lang {
  if (typeof window !== 'undefined') {
    // /zh/* path takes precedence over query / localStorage / navigator
    // because the URL has SEO commitment; any cached preference is stale
    // relative to it.
    const path = window.location.pathname;
    if (path === '/zh' || path.startsWith('/zh/')) return 'zh';

    // EN-mirror URLs (/, /app/privacy, /app/terms) are SEO-committed to en
    // — the prerendered HTML is en. A zh-preferring user landing here must
    // hydrate as en to match the static HTML; otherwise React swaps text
    // to Chinese on hydration, producing a visible language flash and
    // schema/content mismatch for crawlers (round-4 Codex review P2 @:22).
    // Toggling via the lang switch navigates them to the /zh mirror.
    // Trailing slash stripped to match LangProvider.normalizePath semantics.
    const normalized = normalizePublicPath(path);
    if (isPublicEnMirror(normalized)) return 'en';

    const urlParam = new URL(window.location.href).searchParams.get('lang');
    if (urlParam === 'en' || urlParam === 'zh') return urlParam;
    const stored = localStorage.getItem('popwiz_lang');
    if (stored === 'en' || stored === 'zh') return stored;
    return navigator.language.startsWith('zh') ? 'zh' : 'en';
  }
  return 'en';
}

/** Persist language choice. Only call on explicit user action (toggle click). */
export function persistLang(lang: Lang) {
  // localStorage only. URL persistence is the responsibility of
  // LangProvider.setLang (which navigates to the public-route counterpart
  // when toggling on /, /app/privacy, /app/terms). The old ?lang=zh query
  // write was a SPA-era workaround; with /zh/* routes it now conflicts
  // with canonical and pollutes nginx/crawler logs.
  localStorage.setItem('popwiz_lang', lang);
}

// React Context for global language state
interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const LangContext = createContext<LangContextValue>({
  lang: 'en',
  setLang: () => {},
});

export function useLang() {
  return useContext(LangContext);
}

/**
 * Prefix internal paths with /zh when current lang is zh AND the path is in
 * the public-route set with a zh mirror. Otherwise returns path unchanged.
 *
 * Used by footer links / breadcrumbs to keep navigation within the same locale.
 */
const PUBLIC_ROUTE_PAIRS: Record<string, string> = {
  '/':            '/zh/',
  '/app/privacy': '/zh/app/privacy',
  '/app/terms':   '/zh/app/terms',
  '/help':         '/zh/help',
};

export function withLangPrefix(path: string, lang: Lang): string {
  if (lang !== 'zh') return path;
  return toZhMirrorPath(path) ?? path;
}

export function normalizePublicPath(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

export function toZhMirrorPath(path: string): string | undefined {
  const normalized = normalizePublicPath(path);
  if (normalized.startsWith('/help/')) return isPrerenderPath(normalized) ? `/zh${normalized}` : undefined;
  return PUBLIC_ROUTE_PAIRS[normalized];
}

export function toEnMirrorPath(path: string): string | undefined {
  const normalized = normalizePublicPath(path);
  if (normalized === '/zh') return '/';
  if (normalized === '/zh/help') return '/help';
  if (normalized.startsWith('/zh/help/')) return isPrerenderPath(normalized) ? normalized.slice(3) : undefined;
  return Object.entries(PUBLIC_ROUTE_PAIRS).find(
    ([, zhPath]) => normalizePublicPath(zhPath) === normalized,
  )?.[0];
}

export function isPublicEnMirror(path: string): boolean {
  const normalized = normalizePublicPath(path);
  return normalized in PUBLIC_ROUTE_PAIRS || (normalized.startsWith('/help/') && isPrerenderPath(normalized));
}

export function isPublicZhMirror(path: string): boolean {
  const normalized = normalizePublicPath(path);
  return normalized === '/zh' || toEnMirrorPath(normalized) !== undefined;
}

/** Pure: if an en-pinned HELP path carries ?lang=zh, return the /zh mirror URL
 * to forward to (lang stripped, other params kept) — else null. The #467
 * en-mirror pin in detectLang() runs before the ?lang= branch, so legacy
 * /help/*?lang=zh links would silently render English (GitHub-codex #467 P2,
 * 2026-07-18 round). Redirecting to the /zh mirror keeps the zh contract AND
 * deterministic hydration. Landing/privacy/terms en-pin predates #467 — out of
 * scope here. Placeholder (non-prerendered) help slugs are not en-pinned and
 * keep the in-place ?lang=zh client render. */
export function helpZhQueryRedirectTarget(pathname: string, search: string): string | null {
  const normalized = normalizePublicPath(pathname);
  if (normalized !== '/help' && !normalized.startsWith('/help/')) return null;
  const params = new URLSearchParams(search);
  if (params.get('lang') !== 'zh') return null;
  const target = toZhMirrorPath(normalized);
  if (!target) return null;
  params.delete('lang');
  const qs = params.toString();
  return qs ? `${target}?${qs}` : target;
}

export { PUBLIC_ROUTE_PAIRS };
